// @section 1 PM route — Vertis Group internal Projects (read-only spike)
//
// Reframed from a flat task firehose to a Projects-first view per Adam's
// 2026-05-27 brainstorm:
//   - Project = the container (a Vertis-internal build, campaign, or
//                operations effort)
//   - Task    = a step inside a project
//   - /pm specifically holds INTERNAL Vertis-Group projects only — BD work
//     lives in each account's BD lens; client delivery lives in each
//     account's Delivery lens. /pm is the home for things that don't
//     belong on a specific client account.
//
// Spike rules — same contract as the Strategy + Workplan-Pipeline spikes:
// read-only, no schema changes, no API additions beyond a thin GET wrapper
// for the existing /api/hq/pm/projects endpoint. If hq_pm.projects is empty
// (likely — the table has existed but nothing's been written), the view
// falls back to hardcoded placeholder projects so the LAYOUT is real and
// Adam can evaluate it. Once the layout's validated, the next pass adds
// write surfaces, a category column, and the Strategy.CriticalInitiative ↔
// Project link.

import type { CSSProperties } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { listProjects, listTasks, type Project, type Task } from '../api';
import { useMeUser } from '../lib/useMe';
import { useIsMobile } from '../lib/useIsMobile';

type StatusKey = 'active' | 'paused' | 'backlog' | 'done';
type ScopeKey = 'all' | 'mine';

// @block 1.1 Placeholder projects — shown if hq_pm.projects is empty so the
// layout is visible against grounded Vertis context. Each one's a project
// Adam's actually running internally right now (per session memory + current
// brainstorm). Categories match the chip filter set.
type Placeholder = {
  id: string;
  name: string;
  category: 'Product' | 'Platform' | 'BD assets' | 'Operations' | 'People';
  owner: string;
  status: StatusKey;
  tasksDone: number;
  tasksTotal: number;
  dueDate?: string;
  lastUpdate?: string;
  topTasks?: { title: string; priority: 1 | 2 | 3; due?: string; assignee?: string }[];
};

const PLACEHOLDER_PROJECTS: Placeholder[] = [
  {
    id: 'ph-atlas-merger',
    name: 'Atlas CRM+ERP merger to prod',
    category: 'Product',
    owner: 'Adam',
    status: 'active',
    tasksDone: 9,
    tasksTotal: 14,
    dueDate: '2026-05-30',
    lastUpdate: 'today',
    topTasks: [
      { title: 'Workplan Pipeline iteration', priority: 1, due: 'today', assignee: 'Adam' },
      { title: 'Strategy spike completion', priority: 2, due: 'today', assignee: 'Adam' },
      { title: 'Final prod swap (one-shot)', priority: 1, due: '2026-05-30', assignee: 'Adam' },
    ],
  },
  {
    id: 'ph-cpa-seq',
    name: 'CPA/BK email sequence v2',
    category: 'BD assets',
    owner: 'Patrick',
    status: 'active',
    tasksDone: 3,
    tasksTotal: 8,
    lastUpdate: '2 days ago',
    topTasks: [
      { title: 'Rewrite T1 (net-new) — no link version', priority: 1, assignee: 'Patrick' },
      { title: 'Audit T2/T3 for engagement-lift', priority: 2, assignee: 'Patrick' },
    ],
  },
  {
    id: 'ph-mary-onboard',
    name: 'Mary onboarding',
    category: 'People',
    owner: 'Adam',
    status: 'active',
    tasksDone: 4,
    tasksTotal: 7,
    lastUpdate: 'yesterday',
    topTasks: [
      { title: 'Atlas user account + DWD scope', priority: 2, assignee: 'Adam' },
      { title: 'First demo-buildout assignment', priority: 3, assignee: 'Mary' },
    ],
  },
  {
    id: 'ph-bridge-conn-inv',
    name: 'Bridge API connector inventory',
    category: 'Platform',
    owner: 'Adam',
    status: 'active',
    tasksDone: 5,
    tasksTotal: 9,
    lastUpdate: '4 days ago',
  },
  {
    id: 'ph-optiqos-demo',
    name: 'OptiqOS Strategy module demo polish',
    category: 'Product',
    owner: 'Adam',
    status: 'paused',
    tasksDone: 2,
    tasksTotal: 6,
    lastUpdate: 'last week',
  },
  {
    id: 'ph-onboard-kit',
    name: 'New client onboarding kit',
    category: 'Operations',
    owner: 'Adam',
    status: 'backlog',
    tasksDone: 0,
    tasksTotal: 5,
  },
  {
    id: 'ph-billing-auto',
    name: 'Quarterly billing review automation',
    category: 'Operations',
    owner: 'Adam',
    status: 'backlog',
    tasksDone: 0,
    tasksTotal: 4,
  },
  {
    id: 'ph-dkim-refactor',
    name: 'DKIM monitor refactor',
    category: 'Platform',
    owner: 'Adam',
    status: 'backlog',
    tasksDone: 0,
    tasksTotal: 3,
  },
  {
    id: 'ph-slice-a',
    name: 'Atlas Slice A landing (Overview lens)',
    category: 'Product',
    owner: 'Adam',
    status: 'done',
    tasksDone: 5,
    tasksTotal: 5,
    lastUpdate: '1 day ago',
  },
  {
    id: 'ph-facts-redesign',
    name: 'BD Facts card-grid redesign',
    category: 'Product',
    owner: 'Adam',
    status: 'done',
    tasksDone: 3,
    tasksTotal: 3,
    lastUpdate: 'today',
  },
];

const CATEGORIES: Placeholder['category'][] = ['Product', 'Platform', 'BD assets', 'Operations', 'People'];

export default function Pm() {
  const me = useMeUser();
  // @block 1.2 Phone: own the scroll + add gutters. The canvas is overflow:hidden,
  // so the grouped project list would otherwise clip and not scroll on a phone.
  const isMobile = useIsMobile();
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [_tasks, setTasks] = useState<Task[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [scope, setScope] = useState<ScopeKey>('all');
  const [statusFilter, setStatusFilter] = useState<StatusKey | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<Placeholder['category'] | 'all'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([listProjects(), listTasks()])
      .then(([p, t]) => { setProjects(p); setTasks(t); })
      .catch((e) => setErr(String(e)));
  }, []);

  // If hq_pm.projects has real rows, use them; else fall back to placeholders
  // so the layout is visible. The fall-back is the spike's whole point.
  const usingPlaceholders = !projects || projects.length === 0;
  const allItems: Placeholder[] = useMemo(() => {
    if (!usingPlaceholders) {
      // Map real Project rows into the display shape. Categories + task
      // counts aren't on the table yet, so they're synthesized.
      return projects!.map((p) => ({
        id: p.id,
        name: p.name,
        category: 'Product' as const, // placeholder until category column exists
        owner: me?.displayName ?? 'Adam',
        status: (p.status === 'active' ? 'active'
                : p.status === 'paused' ? 'paused'
                : p.status === 'done' ? 'done'
                : 'backlog') as StatusKey,
        tasksDone: 0,
        tasksTotal: 0,
        lastUpdate: p.endsOn ?? undefined,
      }));
    }
    return PLACEHOLDER_PROJECTS;
  }, [projects, usingPlaceholders, me]);

  // Apply scope + status + category filters
  const filtered = useMemo(() => {
    return allItems.filter((p) => {
      if (scope === 'mine' && me) {
        // Best-effort owner match (placeholders use display-name strings).
        if (!p.owner.toLowerCase().includes((me.displayName ?? '').split(' ')[0].toLowerCase())) return false;
      }
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (categoryFilter !== 'all' && p.category !== categoryFilter) return false;
      return true;
    });
  }, [allItems, scope, statusFilter, categoryFilter, me]);

  // Group by status with a stable order
  const grouped = useMemo(() => {
    const order: StatusKey[] = ['active', 'paused', 'backlog', 'done'];
    const m = new Map<StatusKey, Placeholder[]>();
    for (const p of filtered) {
      if (!m.has(p.status)) m.set(p.status, []);
      m.get(p.status)!.push(p);
    }
    return order.filter((s) => m.has(s)).map((s) => [s, m.get(s)!] as const);
  }, [filtered]);

  return (
    <div style={{
      display: 'grid', gap: 14, maxWidth: 1100,
      ...(isMobile ? { height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px 14px' } : null),
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, lineHeight: 1.1 }}>Projects</h1>
          <div style={{ fontSize: 12, color: '#6b7a90', marginTop: 4 }}>
            Vertis Group internal builds, BD assets, operations, and platform work — distinct from BD pipeline (account → BD lens) and client delivery (account → Delivery lens).
          </div>
        </div>
        <button style={primaryBtnStyle} title="Coming next pass">+ New project</button>
      </div>

      {usingPlaceholders && (
        <div style={{
          padding: '10px 14px', border: '1px dashed #2a3346', borderRadius: 6,
          fontSize: 11, color: '#a3b1c2', lineHeight: 1.6,
        }}>
          <strong style={{ color: '#e6e8ee' }}>Spike · read-only.</strong> No real projects exist in{' '}
          <code>hq_pm.projects</code> yet — showing placeholder cards grounded in your actual
          current work so the layout is visible. Next pass: write surface for project create + the{' '}
          <code>category</code> column.
        </div>
      )}

      {err && <div style={{ color: '#e87e7e', fontSize: 12 }}>{err}</div>}

      {/* Filter strip */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <FilterGroup label="Scope">
          <Chip active={scope === 'all'} onClick={() => setScope('all')}>All</Chip>
          {me && <Chip active={scope === 'mine'} onClick={() => setScope('mine')}>Mine</Chip>}
        </FilterGroup>
        <FilterGroup label="Status">
          <Chip active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>All</Chip>
          <Chip active={statusFilter === 'active'} onClick={() => setStatusFilter('active')}>Active</Chip>
          <Chip active={statusFilter === 'paused'} onClick={() => setStatusFilter('paused')}>Paused</Chip>
          <Chip active={statusFilter === 'backlog'} onClick={() => setStatusFilter('backlog')}>Backlog</Chip>
          <Chip active={statusFilter === 'done'} onClick={() => setStatusFilter('done')}>Done</Chip>
        </FilterGroup>
        <FilterGroup label="Category">
          <Chip active={categoryFilter === 'all'} onClick={() => setCategoryFilter('all')}>All</Chip>
          {CATEGORIES.map((c) => (
            <Chip key={c} active={categoryFilter === c} onClick={() => setCategoryFilter(c)}>{c}</Chip>
          ))}
        </FilterGroup>
      </div>

      {/* Status sections */}
      <div style={{ display: 'grid', gap: 14 }}>
        {grouped.length === 0 && (
          <em style={{ color: '#6b7a90', fontSize: 13 }}>No projects match these filters.</em>
        )}
        {grouped.map(([status, list]) => (
          <StatusSection key={status} status={status} count={list.length}>
            {list.map((p) => (
              <ProjectCard
                key={p.id}
                project={p}
                expanded={expandedId === p.id}
                onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)}
              />
            ))}
          </StatusSection>
        ))}
      </div>
    </div>
  );
}

// @section 2 Status section — collapsible group of project cards
function StatusSection({ status, count, children }: { status: StatusKey; count: number; children: React.ReactNode }) {
  const meta = STATUS_META[status];
  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <span style={{
          fontSize: 9, padding: '2px 7px', borderRadius: 3,
          background: `${meta.tone}22`, color: meta.tone,
          textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700,
        }}>
          {meta.label}
        </span>
        <span style={{ fontSize: 11, color: '#6b7a90' }}>{count}</span>
      </div>
      <div style={{ display: 'grid', gap: 10 }}>{children}</div>
    </section>
  );
}

// @section 3 Project card — header + progress + meta + expandable task preview
function ProjectCard({ project, expanded, onToggle }: { project: Placeholder; expanded: boolean; onToggle: () => void }) {
  const pct = project.tasksTotal === 0 ? 0 : Math.round((project.tasksDone / project.tasksTotal) * 100);
  const meta = STATUS_META[project.status];
  const isDone = project.status === 'done';
  const isOverdue = project.dueDate && !isDone && project.dueDate < new Date().toISOString().slice(0, 10);

  return (
    <div
      style={{
        border: '1px solid #232a37',
        borderRadius: 8,
        background: '#171c26',
        overflow: 'hidden',
      }}
    >
      <div
        style={{ padding: '12px 14px', cursor: 'pointer' }}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 12, color: '#6b7a90' }}>{expanded ? '▾' : '▸'}</span>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#e6e8ee' }}>{project.name}</div>
            <CategoryChip category={project.category} />
          </div>
          <div style={{ fontSize: 12, color: '#a3b1c2', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {project.tasksDone} of {project.tasksTotal} tasks
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ marginTop: 10, height: 6, background: '#232a37', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${pct}%`,
            background: isDone ? '#6ec78a' : meta.tone,
            transition: 'width 200ms',
          }} />
        </div>

        {/* Meta line */}
        <div style={{
          marginTop: 8, fontSize: 11, color: '#6b7a90',
          display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
        }}>
          <span style={{ textTransform: 'uppercase', letterSpacing: 1 }}>{project.owner}</span>
          {project.dueDate && (
            <span style={{ color: isOverdue ? '#e87e7e' : '#6b7a90' }}>
              due {project.dueDate}{isOverdue ? ' · overdue' : ''}
            </span>
          )}
          {project.lastUpdate && <span>last update · {project.lastUpdate}</span>}
          <span style={{ marginLeft: 'auto', color: meta.tone, fontWeight: 600 }}>{pct}%</span>
        </div>
      </div>

      {/* Expanded task preview */}
      {expanded && (
        <div style={{ borderTop: '1px solid #232a37', padding: '12px 14px', background: '#1d2330' }}>
          {project.topTasks && project.topTasks.length > 0 ? (
            <>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5, color: '#a3b1c2', marginBottom: 8 }}>
                Open tasks ({project.tasksTotal - project.tasksDone})
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
                {project.topTasks.map((t, i) => (
                  <li key={i} style={{
                    display: 'flex', alignItems: 'baseline', gap: 8,
                    padding: '6px 8px', background: '#171c26',
                    borderRadius: 4, fontSize: 12,
                  }}>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
                      background: t.priority === 1 ? '#e87e7e22' : t.priority === 2 ? '#f0c67422' : '#a3b1c222',
                      color:      t.priority === 1 ? '#e87e7e' : t.priority === 2 ? '#f0c674' : '#a3b1c2',
                    }}>
                      P{t.priority}
                    </span>
                    <span style={{ color: '#e6e8ee', flex: 1 }}>{t.title}</span>
                    {t.due && <span style={{ color: '#6b7a90', fontSize: 10 }}>{t.due}</span>}
                    {t.assignee && <span style={{ color: '#7eb6e8', fontSize: 10 }}>@{t.assignee}</span>}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <em style={{ fontSize: 12, color: '#6b7a90' }}>
              No task preview wired yet for this project — placeholder card.
            </em>
          )}

          <div style={{ fontSize: 10, color: '#6b7a90', marginTop: 10, fontStyle: 'italic' }}>
            Drill into the full task list (with inline create + dependencies + time tracking) comes in the next pass.
          </div>
        </div>
      )}
    </div>
  );
}

// @section 4 Helper bits
function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5, color: '#6b7a90' }}>
        {label}
      </span>
      <div style={{ display: 'flex', gap: 4 }}>
        {children}
      </div>
    </div>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontSize: 11, padding: '3px 10px',
        border: `1px solid ${active ? '#7eb6e8' : '#2a3346'}`,
        borderRadius: 99,
        background: active ? '#7eb6e822' : 'transparent',
        color: active ? '#e6e8ee' : '#a3b1c2',
        cursor: 'pointer', fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

function CategoryChip({ category }: { category: Placeholder['category'] }) {
  const tone = category === 'Product' ? '#7eb6e8'
    : category === 'Platform' ? '#a08fd8'
    : category === 'BD assets' ? '#f0c674'
    : category === 'Operations' ? '#6ec78a'
    : '#f0a96b'; // People
  return (
    <span style={{
      fontSize: 9, padding: '2px 6px', borderRadius: 3,
      background: `${tone}22`, color: tone,
      textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600,
    }}>
      {category}
    </span>
  );
}

const STATUS_META: Record<StatusKey, { label: string; tone: string }> = {
  active:  { label: 'Active',  tone: '#7eb6e8' },
  paused:  { label: 'Paused',  tone: '#f0c674' },
  backlog: { label: 'Backlog', tone: '#a3b1c2' },
  done:    { label: 'Done',    tone: '#6ec78a' },
};

const primaryBtnStyle: CSSProperties = {
  fontSize: 12, padding: '7px 14px',
  border: '1px solid #7eb6e8', borderRadius: 5,
  background: '#7eb6e822', color: '#e6e8ee',
  cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
};
